import { useQueryClient } from "@tanstack/react-query";
import { Building2, Check, ChevronDown } from "lucide-react";
import { useRefresh } from "ra-core";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

import { useWorkspaces } from "../workspace/useWorkspaces";
import { clearActiveWorkspaceRoleCache } from "../workspace/activeWorkspaceRole";

/**
 * Workspace/company selector backed by the real Gravun workspace model.
 * Selection is presentation state only, RLS scopes every query to the
 * user's memberships regardless of what is selected here.
 */
export const WorkspaceSelector = () => {
  const { memberships, activeWorkspace, setActiveWorkspaceId } =
    useWorkspaces();
  const queryClient = useQueryClient();
  const refresh = useRefresh();

  const switchWorkspace = (workspaceId: string) => {
    setActiveWorkspaceId(workspaceId);
    // Role-based UI gating must re-resolve under the new workspace.
    clearActiveWorkspaceRoleCache();
    // Every list/query re-fetches under the new workspace scope.
    queryClient.invalidateQueries();
    refresh();
  };

  if (!activeWorkspace) return null;

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 rounded-md border-border bg-card px-2.5 text-[13px] font-medium text-foreground shadow-none hover:bg-accent"
        >
          <Building2 className="size-3.5 text-muted-foreground" />
          <span className="max-w-36 truncate">
            {activeWorkspace.workspaceName}
          </span>
          <ChevronDown className="size-3.5 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60 rounded-lg">
        <DropdownMenuLabel className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Workspaces
        </DropdownMenuLabel>
        {memberships.map((membership) => (
          <DropdownMenuItem
            key={membership.workspaceId}
            onClick={() => switchWorkspace(membership.workspaceId)}
            className="gap-2"
          >
            <div className="flex size-6 items-center justify-center rounded-md bg-primary/10 text-[11px] font-semibold text-primary">
              {membership.workspaceName[0]?.toUpperCase()}
            </div>
            <div className="flex flex-1 flex-col">
              <span className="text-[13px]">{membership.workspaceName}</span>
              <span className="text-[11px] text-muted-foreground">
                {membership.roleName}
              </span>
            </div>
            <Check
              className={cn(
                "size-4",
                membership.workspaceId !== activeWorkspace.workspaceId &&
                  "invisible",
              )}
            />
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          disabled
          className="text-[12px] text-muted-foreground"
        >
          Create workspace, coming soon
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
